// --------------------------------------------------------------
// Created On: 2025-05-07
// Author: Hannah Vaughan
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import styles from "./DigitalPage.module.scss";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import DigitalSensor from "./DigitalSensor/DigitalSensor";
import { DIGITAL_INPUTS } from "src/constants/attributes";
import WarningList from "src/components/WarningList/WarningList";

// Page of the Asset Management Wizard that allows the user to optionally add digital sensor info.
export default function DigitalPage(props: Props): Component {
  const sensorPositions = useMemo(() => getSensorPositions(props.sensorCount), [props.sensorCount]);
  const localSensorPositionsInUse = useMemo(
    () => getLocalSensorPositionsInUse(props.digitalSensorCards),
    [JSON.stringify(props.digitalSensorCards)]
  );
  const [mainWarningMessage, warningMessages] = useMemo(
    () => getWarningMessages(props.digitalSensorsInUse),
    [JSON.stringify(props.digitalSensorsInUse)]
  );

  // Generates an array of numbers from 1 to X, where X is the max sensor count for digital sensors.
  function getSensorPositions(sensorCount: number): number[] {
    return new Array<number>(sensorCount).fill(0).map((_, i) => i + 1);
  }

  // Gathers a list of all analog sensor positions that are in use by the current asset.
  function getLocalSensorPositionsInUse(digitalSensorCards: DigitalSensorCard[]): number[] {
    const newLocalSensorPositionsInUse: number[] = [];
    digitalSensorCards.forEach((card) => {
      newLocalSensorPositionsInUse.push(card.sensorNumber);
    });
    return newLocalSensorPositionsInUse;
  }

  // Populate the list of warnings whenever there are any digital sensors in use by other assets in the network.
  function getWarningMessages(digitalSensorsInUse: SensorInfoMap | null): [string, string[]] {
    let newMainWarningMessage = "";
    const newWarningMessages: string[] = [];
    if (digitalSensorsInUse !== null && Object.keys(digitalSensorsInUse).length > 0) {
      newMainWarningMessage = "The following digital sensors are already in use:";
      Object.entries(digitalSensorsInUse).forEach(([sensorPosition, sensor]) => {
        const formattedSensor: SensorInfo = sensor as SensorInfo;
        newWarningMessages.push(
          `Position #${sensorPosition} is already reporting '${formattedSensor.reporting}' ` +
            `for asset '${formattedSensor.assetName}'.`
        );
      });
    }
    return [newMainWarningMessage, newWarningMessages];
  }

  return (
    <div data-test="digital-sensors-page">
      <div className="mx-1 my-2">
        <label className={styles.title}>
          <span className="ms-3 me-2">Digital Sensors</span>
          <IconTooltip
            id="digital-sensors-tooltip"
            icon="info-circle"
            message={
              "Describes the specific type of digital sensors connected to the monitoring device and " +
              "the type of data that each reports."
            }
            color="var(--info-tooltip)"
          />
        </label>
        <button
          data-test="add-digital-sensor-button"
          type="submit"
          className={`${styles.addButton} btn btn-success float-end`}
          onClick={() => props.onCreate()}
          disabled={
            props.digitalSensorCards.length >= props.sensorCount ||
            (props.digitalSensorsInUse !== null &&
              props.digitalSensorsInUse !== undefined &&
              Object.keys(props.digitalSensorsInUse).length >= props.sensorCount) ||
            props.disabled
          }
        >
          <span className="d-none d-sm-inline">Add Digital Sensor</span>
          <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
        </button>
      </div>
      {props.digitalSensorCards.map((card, i) => (
        <DigitalSensor
          key={card.formId}
          formIndex={i}
          formId={card.formId}
          sensorNumber={card.sensorNumber}
          sourceStateAttribute={card.sensorNumber > 0 ? DIGITAL_INPUTS[card.sensorNumber - 1].stateAttribute : ""}
          isRented={props.isRented}
          sensorPositions={sensorPositions}
          digitalSensorMap={props.digitalSensorMap}
          digitalSensors={props.digitalSensors}
          digitalSensorsInUse={props.digitalSensorsInUse}
          localSensorPositionsInUse={localSensorPositionsInUse}
          disabled={props.disabled}
          onCreate={() => props.onCreate()}
          onChange={(formId, sensorNumber, digitalSensorId, attributeStateCode, reportingDescription) =>
            props.onChange(formId, sensorNumber, digitalSensorId, attributeStateCode, reportingDescription)
          }
          onDelete={(formId, sensorNumber, attributeStateCode) =>
            props.onDelete(formId, sensorNumber, attributeStateCode)
          }
        />
      ))}
      {mainWarningMessage.length > 0 && warningMessages.length > 0 && (
        <div className="m-3">
          <WarningList mainMessage={mainWarningMessage} messages={warningMessages} />
        </div>
      )}
    </div>
  );
}

interface Props {
  isRented: boolean;
  sensorCount: number;
  digitalSensorMap: DigitalSensorMap;
  digitalSensors: DigitalSensor[];
  digitalSensorCards: DigitalSensorCard[];
  digitalSensorsInUse: SensorInfoMap | null;
  disabled: boolean;
  onCreate: () => void;
  onChange: (
    formId: number,
    sensorNumber: number,
    digitalSensorId: number | null,
    attributeStateCode: string,
    reportingDescription: string
  ) => void;
  onDelete: (formId: number, sensorNumber: number, attributeStateCode: string) => void;
}

interface DigitalSensorCard {
  formId: number;
  sensorNumber: number;
  digitalSensorId: number | null;
  attributeStateCode: string;
  reportingDescription: string;
}

interface DigitalSensor {
  digitalSensorId: number;
  name: string;
  applications: DigitalApplication[];
}

interface DigitalApplication {
  attributeStateId: number;
  attributeFlowId: number;
  attributeAccumulationId: number;
  reportingDescription: string;
}

interface DigitalSensorMap {
  [key: string]: DigitalSensorSelection;
}

interface DigitalSensorSelection {
  digitalSensorId: number;
  attributeStateCode: string;
  attributeFlowCode: string;
  attributeAccumulationCode: string;
  attributeModeCode: string;
  attributeKFactorCode: string;
  attributeOffsetCode: string;
  attributeDebounceCode: string;
  attributeUnitsPerPulseCode: string;
  mappedAttributeStateId: number;
  mappedAttributeFlowId: number;
  mappedAttributeAccumulationId: number;
  reportingDescription: string;
}

interface SensorInfoMap {
  [sensorNumber: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}
